import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  signal,
} from '@angular/core'
import { FormField } from '@angular/forms/signals'

import { omit as _omit } from 'lodash'

import { FlxButtonModule, FlxFormControlModule, FlxModalModule } from '@flowx/angular-ui-toolkit'

import { FlxColorsShadesEditorComponent } from './colors-shades-editor/colors-shades-editor.component'

import { createThemeSectionForm } from '../../../services/theme-section-form'
import { ColorForm, MAIN_COLOR_SHADE } from '../color.entity'
import { ThemeColorShades, getShadesFormHex, getThemeColorFromShades } from '../color.helpers'
import { FlxThemeColorService } from '../color.service'
import { FlxHexColorMaskDirective } from '../hex-color-mask.directive'

@Component({
  selector: 'flx-theme-colors-form',
  imports: [
    FormField,
    FlxFormControlModule,
    FlxButtonModule,
    FlxColorsShadesEditorComponent,
    FlxHexColorMaskDirective,
    FlxModalModule,
  ],
  templateUrl: './theme-colors-form.component.html',
  styleUrls: ['./theme-colors-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flx-theme-colors-form',
  },
})
export class FlxThemeColorsFormComponent {
  readonly #colorService = inject(FlxThemeColorService)
  readonly #selectedColorPath = signal<string | null>(null)

  readonly colorData = createThemeSectionForm<ColorForm[]>({
    view: this.#colorService.formData,
    write: (next, previous) =>
      next
        .filter(
          (color, index) => color.fg[MAIN_COLOR_SHADE] !== previous[index]?.fg[MAIN_COLOR_SHADE]
        )
        .forEach((color) => this.#colorService.updateData(this.#withGeneratedShades(color))),
  }).view

  /** The colour whose shades are being edited, with its current values. */
  readonly selectedColor = computed<ColorForm | null>(() => {
    const path = this.#selectedColorPath()
    const color = this.#colorService.formData()?.find((item) => item.path === path)
    return color ?? null
  })

  readonly MAIN_COLOR_SHADE = MAIN_COLOR_SHADE

  selectColor(path: string | null): void {
    this.#selectedColorPath.set(path)
  }

  updateShades(newShades: Partial<ThemeColorShades>, color: ColorForm): void {
    this.#colorService.updateData({ ...color, fg: { ...color.fg, ...newShades } })
  }

  /**
   * Regenerates every shade from a new main (500) shade, keeping the typed main value as is, as
   * the colour input did before.
   */
  #withGeneratedShades(color: ColorForm): ColorForm {
    const mainHex = color.fg[MAIN_COLOR_SHADE]
    const generated = _omit(getThemeColorFromShades(getShadesFormHex(mainHex)), [MAIN_COLOR_SHADE])
    return { ...color, fg: { ...color.fg, ...generated } }
  }
}
